import React from 'react';
import { Box, Button, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u7_x5_rinck';
const IMAGE_BASE = '/assets/drawable/';
const IMAGE_KEYS = ['img_0'];
const IMAGE_KEYS_DYNAMIC = ['img_0'];

interface RinckModulInfo {
  name: string;
  artikelBezeichnung: string;
  cNummer?: string;
  titel: string;
  hauptfunktion: string;
  beschreibungDetail: string;
  anschlussklemmenUebersicht: string;
  klemmenbelegungDetail: string;
  technischeDaten: string;
  besondereFunktionenOderHinweise: string;
  hersteller?: string;
}

const rinckModule: RinckModulInfo[] = [
  {
    name: 'MIO_M (Multi IO Master)',
    artikelBezeichnung: 'MIO_M',
    titel: 'Rinck MIO_M – Multi IO Master',
    hauptfunktion: 'Zentrale IO-Erweiterung mit gemischten Ein-/Ausgängen für SPS/GA.',
    beschreibungDetail: 'Modulares IO-Modul, kombiniert digitale Eingänge/Ausgänge und ggf. analoge Kanäle (modellabhängig).',
    anschlussklemmenUebersicht: 'X1: DO1–DO8; X2: DI1–DI16; X3: Versorgung; ...',
    klemmenbelegungDetail: 'DO: 24V/0V, Lastanschluss über Transistor/Relais je nach Ausführung. DI: Bezug 0V, Schaltschwellen beachten.',
    technischeDaten: 'Versorgung 24V DC; Kanaldaten lt. Datenblatt (DI-Schwelle, DO-Strom, EMV).',
    besondereFunktionenOderHinweise: 'Adressierung, Diagnose-LEDs, Steckklemmen, Erweiterbarkeit.',
    hersteller: 'Rinck Elektronik / SE-GA'
  },
  {
    name: 'DI16_S (16 Digitale Eingänge)',
    artikelBezeichnung: 'DI16_S',
    titel: 'Rinck DI16_S – Digitale Eingänge',
    hauptfunktion: 'Erfassung von 24 V DC Signalen (z. B. Taster, Meldekontakte).',
    beschreibungDetail: '16-kanaliges Digitaleingangsmodul mit gemeinsamen 0V-Bezug.',
    anschlussklemmenUebersicht: 'X1: DI1–DI8; X2: DI9–DI16; X3: 0V Sammelrückleiter.',
    klemmenbelegungDetail: 'Jeder Eingang gegen 0V; Entprellung/Filter möglich; Status-LED je Kanal.',
    technischeDaten: 'Schaltschwellen typ. HIGH > 15 V; Eingangsstrom wenige mA.',
    besondereFunktionenOderHinweise: 'Gruppenweise Sammel-0V; EMV-gerechte Verdrahtung; Leitungsabschirmung bei langen Wegen.',
    hersteller: 'Rinck Elektronik / SE-GA'
  },
  {
    name: 'R8_FK (8 Relaisausgänge)',
    artikelBezeichnung: 'R8_FK',
    titel: 'Rinck R8_FK – 8 Relaisausgänge',
    hauptfunktion: 'Schalten von 230 V AC / 24 V DC Lasten über potentialfreie Kontakte.',
    beschreibungDetail: '8 Kanäle, je Kanal Wechsler/Schließer (modellabhängig). Trennung Steuer-/Lastseite.',
    anschlussklemmenUebersicht: 'X1–X8 jeweils COM/NO/NC; X9: Versorgung 24V DC für Spulen.',
    klemmenbelegungDetail: 'Spulenseitig A1/A2; Kontaktseite gemäß Last. Schutzbeschaltung (RC, Varistor) bei induktiver Last.',
    technischeDaten: 'Kontaktstrom je nach Relais (z. B. 6–10 A AC1). Spulenspannung 24 V DC.',
    besondereFunktionenOderHinweise: 'Austauschrelais, LED-Indikatoren, Handschalter optional.',
    hersteller: 'Rinck Elektronik / SE-GA'
  }
];

const introBlocks = [
  {
    heading: 'Rinck I/O-Module – Überblick',
    body: 'Rinck-Module erweitern SPS/GA-Systeme um Ein-/Ausgänge. Typische Module: DI-Module, DO/Relais-Module sowie kombinierte IO-Master. Wichtige Aspekte: Klemmenbelegung, Versorgung, EMV-gerechte Verdrahtung.'
  },
  {
    heading: 'Planung und Verdrahtung',
    body: 'Kanalströme und Kontaktarten beachten, Sammelschienen (0V/24V) sauber führen, Schirmkonzept und Trennung von Steuer-/Lastkreisen einhalten.'
  }
];

const M6_U7_X5_Rinck: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const getImageSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    let s = String(v).trim();
    if (s.includes('/')) {
      s = s.split('/').pop() || s;
    }
    return `/assets/unit_custom/${s}`;
  };

  const getDynamicImageSrc = (selectedType: string, idx: number, fallback: string) => {
    const key = `dyn_${selectedType}_img_${idx}`;
    return getImageSrc(key, fallback);
  };

  const [selectedModule, setSelectedModule] = React.useState<string>(rinckModule[0].name);
  const current = rinckModule.find(r => r.name === selectedModule) || rinckModule[0];

  const mergedIntroBlocks = React.useMemo(
    () => introBlocks.map((tb, i) => ({
      ...tb,
      body: override?.texts?.[`intro_${i}`] || tb.body
    })),
    [override]
  );

  const mergedDynamicBlocks = React.useMemo(() => [
    { heading: current.titel, body: override?.texts?.[`dyn_${selectedModule}_0`] || current.beschreibungDetail },
    { heading: 'Hauptfunktion', body: override?.texts?.[`dyn_${selectedModule}_1`] || current.hauptfunktion },
    { heading: 'Anschlussklemmen – Übersicht', body: override?.texts?.[`dyn_${selectedModule}_2`] || current.anschlussklemmenUebersicht },
    { heading: 'Klemmenbelegung – Details', body: override?.texts?.[`dyn_${selectedModule}_3`] || current.klemmenbelegungDetail },
    { heading: 'Technische Daten', body: override?.texts?.[`dyn_${selectedModule}_4`] || current.technischeDaten },
    { heading: 'Besondere Funktionen/Hinweise', body: override?.texts?.[`dyn_${selectedModule}_5`] || current.besondereFunktionenOderHinweise }
  ], [selectedModule, override, current]);

  const allTextBlocks = React.useMemo(
    () => [...mergedIntroBlocks, ...mergedDynamicBlocks],
    [mergedIntroBlocks, mergedDynamicBlocks]
  );

  const fullPlainText = React.useMemo(() =>
    allTextBlocks.map(b => `${b.heading}. ${b.body}`).join(' '),
    [allTextBlocks]
  );

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Bild-Upload:', error);
      alert('Fehler beim Hochladen des Bildes.');
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      await refresh();
    } catch (error) {
      console.error('Fehler beim Text-Speichern:', error);
      alert('Fehler beim Speichern des Textes.');
    }
  }, [token, refresh]);

  const handleTypeChange = (event: any) => {
    setSelectedModule(String(event.target.value));
  };

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.7.5 Rinck I/O Module', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        IMAGE_KEYS.map((key) =>
          React.createElement(ZoomableImage, {
            key: key,
            src: getImageSrc(key, `${IMAGE_BASE}zzz_help.png`),
            alt: 'Rinck Modul',
            label: 'Modulbild',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(key, file) : undefined
          })
        )
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(EditableTextBlock, { key: 0, pageId: PAGE_ID, blockId: 'intro_0', heading: mergedIntroBlocks[0].heading, body: mergedIntroBlocks[0].body, onSave: (newBody: string) => handleTextSave('intro_0', newBody) }),
        React.createElement(EditableTextBlock, { key: 1, pageId: PAGE_ID, blockId: 'intro_1', heading: mergedIntroBlocks[1].heading, body: mergedIntroBlocks[1].body, onSave: (newBody: string) => handleTextSave('intro_1', newBody) }),
        React.createElement(FormControl, { fullWidth: true, sx: { mt: 2, mb: 2 } },
          React.createElement(InputLabel, { id: 'rinck-select-label' }, 'Modul auswählen'),
          React.createElement(Select, {
            labelId: 'rinck-select-label',
            id: 'rinck-select',
            value: selectedModule,
            label: 'Modul auswählen',
            onChange: handleTypeChange
          },
            ...rinckModule.map((m) =>
              React.createElement(MenuItem, { key: m.name, value: m.name }, m.name)
            )
          )
        ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          IMAGE_KEYS_DYNAMIC.map((key) =>
            React.createElement(ZoomableImage, {
              key: key,
              src: getDynamicImageSrc(selectedModule, 0, `${IMAGE_BASE}zzz_help.png`),
              alt: 'Bild für Spinner-Typ',
              label: 'Spinner-Bild',
              onReplace: canUploadImages ? (file: File) => handleImageReplace(`dyn_${selectedModule}_img_0`, file) : undefined
            })
          )
        ),
        React.createElement(EditableTextBlock, { key: `${selectedModule}_0`, pageId: PAGE_ID, blockId: `dyn_${selectedModule}_0`, heading: mergedDynamicBlocks[0]?.heading || 'Allgemeine Beschreibung', body: mergedDynamicBlocks[0]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedModule}_0`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedModule}_1`, pageId: PAGE_ID, blockId: `dyn_${selectedModule}_1`, heading: 'Hauptfunktion', body: mergedDynamicBlocks[1]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedModule}_1`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedModule}_2`, pageId: PAGE_ID, blockId: `dyn_${selectedModule}_2`, heading: 'Anschlussklemmen – Übersicht', body: mergedDynamicBlocks[2]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedModule}_2`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedModule}_3`, pageId: PAGE_ID, blockId: `dyn_${selectedModule}_3`, heading: 'Klemmenbelegung – Details', body: mergedDynamicBlocks[3]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedModule}_3`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedModule}_4`, pageId: PAGE_ID, blockId: `dyn_${selectedModule}_4`, heading: 'Technische Daten', body: mergedDynamicBlocks[4]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedModule}_4`, newBody) }),
        React.createElement(EditableTextBlock, { key: `${selectedModule}_5`, pageId: PAGE_ID, blockId: `dyn_${selectedModule}_5`, heading: 'Besondere Funktionen/Hinweise', body: mergedDynamicBlocks[5]?.body || '', onSave: (newBody: string) => handleTextSave(`dyn_${selectedModule}_5`, newBody) }),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u7_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M6_U7_X5_Rinck;


